import { ColorPicker } from "antd";
import { FormikProps, getIn } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ISurveyDisplayRules } from "../../../../../../../@types/Surveys";
import { Close } from "../../../../../../../assets/icons/Close";
import { Button } from "../../../../../../../components/Button";
import { CustomIconButton } from "../../../../../../../components/CustomIconButton";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import {
  AddRangeOptionButton,
  colorPickerClassName,
  deleteRangeOptionButtonClassName,
  DisplaySubTitle,
  ErrorMessage,
  MultipleInputsRow,
  RowElement,
} from "../../DisplayTab.styles";
import {
  Divider,
  domainTotalScoreSelectCss,
  domainTotalScoreTextFieldCss,
  popupOptionsCss,
} from "./DomainAndTotalScoreForm.styles";

interface IDomainAndTotalScoreFormProps {
  displaySettingsFormik: FormikProps<ISurveyDisplayRules>;
  domainOptions: IOption[];
  addDomainAndTotalLegend: () => void;
}

export function DomainAndTotalScoreForm({
  displaySettingsFormik,
  domainOptions,
  addDomainAndTotalLegend,
}: IDomainAndTotalScoreFormProps) {
  const { t } = useTranslation();

  useEffect(() => {
    displaySettingsFormik.setValues(displaySettingsFormik.initialValues);
  }, [displaySettingsFormik.initialValues]);

  const shouldShowErrorMessage = (
    field: "domain" | "name" | "color",
    index?: number
  ) => {
    if (
      index !== undefined &&
      Array.isArray(displaySettingsFormik.touched.domainAndTotalLegends) &&
      displaySettingsFormik.touched.domainAndTotalLegends?.[index]
    ) {
      return displaySettingsFormik.touched.domainAndTotalLegends?.[index][field]
        ? getIn(
            displaySettingsFormik.errors.domainAndTotalLegends?.[index],
            field
          )
        : "";
    }
    return "";
  };

  const deleteDomainLegendOption = (indexToRemove: number) => {
    displaySettingsFormik.setFieldValue(
      "domainAndTotalLegends",
      displaySettingsFormik.values.domainAndTotalLegends
        ?.filter((_, index) => index !== indexToRemove)
        .map((legend, index) => {
          return { ...legend, order: index };
        })
    );
  };

  return (
    <>
      <Divider />
      {displaySettingsFormik.values.domainAndTotalLegends !== undefined &&
        displaySettingsFormik.values.domainAndTotalLegends
          .sort((a, b) => a.order - b.order)
          .map((_, index) => (
            <MultipleInputsRow key={index}>
              <RowElement>
                <DisplaySubTitle>
                  {t("surveys_builder_display_legendN", { number: index + 1 })}
                </DisplaySubTitle>

                <SelectDropdown
                  className={domainTotalScoreSelectCss()}
                  popUpCsss={popupOptionsCss()}
                  width={248}
                  height={42}
                  showSearch
                  optionFilterProp="label"
                  value={
                    displaySettingsFormik.values.domainAndTotalLegends !==
                    undefined
                      ? displaySettingsFormik.values.domainAndTotalLegends[
                          index
                        ].domain
                      : ""
                  }
                  onValueChange={(value) => {
                    displaySettingsFormik.setFieldValue(
                      `domainAndTotalLegends[${index}].domain`,
                      value.toString()
                    );
                  }}
                  options={domainOptions}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("domain", index)}
                </ErrorMessage>
              </RowElement>
              <RowElement>
                <DisplaySubTitle>
                  {t("surveys_builder_display_legendNameN", {
                    number: index + 1,
                  })}
                </DisplaySubTitle>
                <TextField
                  label=""
                  placeholder={t("surveys_builder_display_addLegend")}
                  name={`domainAndTotalLegends[${index}].name`}
                  value={
                    displaySettingsFormik.values.domainAndTotalLegends?.[index]
                      .name ?? ""
                  }
                  onChange={displaySettingsFormik.handleChange}
                  className={domainTotalScoreTextFieldCss()}
                  backgroudColor="white"
                  onBlur={displaySettingsFormik.handleBlur}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("name", index)}
                </ErrorMessage>
              </RowElement>
              <RowElement>
                <DisplaySubTitle>
                  {t("surveys_builder_display_color")} *
                </DisplaySubTitle>
                <ColorPicker
                  className={colorPickerClassName()}
                  value={
                    displaySettingsFormik.values.domainAndTotalLegends?.[index]
                      .color
                  }
                  onChange={(value) => {
                    displaySettingsFormik.setFieldValue(
                      `domainAndTotalLegends[${index}].color`,
                      value.toHexString()
                    );
                  }}
                  disabledFormat
                  showText
                  defaultValue="black"
                  disabledAlpha
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("color", index)}
                </ErrorMessage>
              </RowElement>
              {(displaySettingsFormik.values.domainAndTotalLegends?.length ??
                0) > 1 && (
                <CustomIconButton
                  className={deleteRangeOptionButtonClassName()}
                  key={index}
                  onClick={() => deleteDomainLegendOption(index)}
                >
                  <Close />
                </CustomIconButton>
              )}
            </MultipleInputsRow>
          ))}
      <RowElement>
        <Button
          type="button"
          label={t("surveys_builder_display_addDomain")}
          className={AddRangeOptionButton()}
          onClick={addDomainAndTotalLegend}
        />
      </RowElement>
    </>
  );
}
